/**
 * useHome — estado, efeitos e ações da Home.
 *
 * A TRADUÇÃO (resposta do servidor → ViewModel) mora em `dashboardVmBuilder.js`, que é puro.
 * Aqui fica só o que precisa de React.
 *
 * ⚠️ Este hook **não conhece card nenhum**. Ele não sabe que existe "domínio" ou "agendamentos":
 * recebe uma lista e executa as ações que vierem dentro dela. É isso que permite o servidor
 * introduzir um card novo sem tocar em uma linha do Admin (decisão D2 do plano).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import homeService from '../../services/homeService';
import { montarVm, CHAVES_COM_DESENHO } from './dashboardVmBuilder';

export default function useHome() {
  const navigate = useNavigate();

  const [home, setHome] = useState(undefined);
  const [carregando, setCarregando] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lidoEm, setLidoEm] = useState(null);

  /** Cards que o cliente escondeu NESTA sessão. Não é regra de negócio — é preferência dele. */
  const [dispensados, setDispensados] = useState([]);

  /**
   * Eventos locais que um card pode disparar (`action.type === 'emit'`).
   *
   * Vocabulário FECHADO: o container registra o que sabe fazer, e um evento desconhecido é
   * ignorado com aviso em vez de quebrar a tela. Assim, um servidor à frente do front (deploy
   * fora de ordem — que já aconteceu aqui) degrada em silêncio em vez de derrubar a Home.
   */
  const manipuladores = useRef({});
  const registrarEvento = useCallback((nome, fn) => { manipuladores.current[nome] = fn; }, []);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) { setCarregando(true); setErrorMessage(null); }
    else setRefreshing(true);
    try {
      const dados = await homeService.getHome();
      setHome(dados);
      setErrorMessage(null);
      setLidoEm(Date.now());
    } catch {
      // No refresh silencioso, manter o que já está na tela: uma falha de rede momentânea não
      // pode apagar a Home inteira.
      if (!silencioso) setErrorMessage('Não foi possível carregar sua página inicial agora.');
    } finally {
      setCarregando(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  /**
   * Card que chegou sem desenho é OMITIDO da tela — decisão do André: melhor não mostrar do que
   * mostrar uma caixa genérica no meio de uma Home desenhada.
   *
   * Mas omitir não pode ser silêncio TOTAL. Deploy fora de ordem (servidor à frente do Admin) já
   * aconteceu aqui, e sem este registro o card simplesmente não existiria para quem estivesse
   * investigando por que ele não aparece.
   */
  useEffect(() => {
    const semDesenho = (home?.cards ?? [])
      .map((c) => c.key)
      .filter((key) => !CHAVES_COM_DESENHO.includes(key));

    if (semDesenho.length > 0) {
      console.warn(`[home] card(s) omitido(s) por falta de desenho: ${semDesenho.join(', ')}`);
    }
  }, [home]);

  /**
   * O ponto de entrada de TODO clique da Home.
   *
   * A View passa o objeto `action` que veio no contrato — ela não sabe para onde vai, e é por
   * isso que um card novo com ação nova não exige tocar na View.
   */
  const onAction = useCallback((action) => {
    if (!action?.type) return;

    switch (action.type) {
      case 'navigate':
        if (action.to) navigate(action.to);
        break;
      case 'external':
        // `noopener,noreferrer`: o link é de conteúdo do cliente e abre em aba nova.
        if (action.to) window.open(action.to, '_blank', 'noopener,noreferrer');
        break;
      case 'emit': {
        const fn = action.event && manipuladores.current[action.event];
        if (fn) fn();
        // Evento que o front ainda não conhece (servidor mais novo que o Admin): ignora sem
        // quebrar. Silêncio total esconderia o problema, então fica registrado.
        else console.warn(`[home] evento sem manipulador: ${action.event}`);
        break;
      }
      default:
        console.warn(`[home] ação desconhecida: ${action.type}`);
    }
  }, [navigate]);

  const vm = montarVm({ home, carregando, errorMessage, refreshing, dispensados, lidoEm });

  const actions = {
    onAction,
    onDismiss: (key) => setDispensados((atuais) => (atuais.includes(key) ? atuais : [...atuais, key])),
    onRefresh: () => carregar(false),
  };

  return { vm, actions, registrarEvento };
}
